import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useAuth } from '@/hooks/useAuth';
import {
  useWorkspaceEntitlements,
  useUpdateWorkspaceEntitlements,
} from '@/hooks/useWorkspaceEntitlements';
import { MODULES, getModuleLabel } from '@/config/modules';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Package } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export default function ModuleAccessPage() {
  const { workspace, workspaceRole, isAdmin, isOwner } = useWorkspace();
  const { user } = useAuth();
  const { toast } = useToast();

  // Check if user can manage modules (owner, admin, or super_admin)
  const canManageModules =
    user?.role === 'super_admin' ||
    isOwner ||
    isAdmin ||
    workspaceRole === 'owner' ||
    workspaceRole === 'admin';

  const { data: entitlements, isLoading, error } = useWorkspaceEntitlements(workspace?.id);
  const updateMutation = useUpdateWorkspaceEntitlements(workspace?.id);

  // Get all module keys from MODULES config
  const allModuleKeys = MODULES.map((m) => m.key);

  // Get enabled modules from entitlements
  const enabledModules = new Set(entitlements?.modules || []);

  const handleToggle = (moduleKey: string) => {
    if (!canManageModules) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to manage modules',
        variant: 'destructive',
      });
      return;
    }

    if (!workspace?.id) {
      toast({
        title: 'Error',
        description: 'Workspace ID is required',
        variant: 'destructive',
      });
      return;
    }

    const next = new Set(enabledModules);
    if (next.has(moduleKey)) {
      next.delete(moduleKey);
    } else {
      next.add(moduleKey);
    }

    updateMutation.mutate([...next], {
      onSuccess: () => {
        toast({
          title: 'Success',
          description: `Module ${next.has(moduleKey) ? 'enabled' : 'disabled'} successfully`,
        });
      },
      onError: (error: any) => {
        toast({
          title: 'Error',
          description: error.message || 'Failed to update module',
          variant: 'destructive',
        });
      },
    });
  };

  // Access control - show 403-style empty state if not authorized
  if (!canManageModules) {
    return (
      <div className="space-y-6 p-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold">Module Access</h1>
          <p className="text-muted-foreground">
            Manage workspace modules and access control
          </p>
        </div>
        <Alert>
          <AlertDescription>
            You do not have permission to manage modules. Only workspace owners and administrators can modify module access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 p-6">
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load modules. Please try again later.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!entitlements) {
    return (
      <div className="space-y-6 p-6">
        <Alert>
          <AlertDescription>
            No entitlements data available. Please try again later.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold">Workspace Modules</h1>
        <p className="text-muted-foreground">
          Enable or disable modules for this workspace. Changes are saved immediately.
        </p>
        {workspace && (
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>Workspace: <span className="font-medium">{workspace.name}</span></span>
            {entitlements.plan?.name && (
              <span>Plan: <span className="font-medium">{entitlements.plan.name}</span></span>
            )}
            {workspaceRole && (
              <span>Role: <span className="font-medium">{workspaceRole}</span></span>
            )}
          </div>
        )}
      </div>

      {/* Modules List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Available Modules
          </CardTitle>
          <CardDescription>
            {allModuleKeys.length} modules available. Toggle to enable or disable for this workspace.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {allModuleKeys.map((moduleKey) => {
              const module = MODULES.find((m) => m.key === moduleKey);
              const label = module?.label || moduleKey;
              const isEnabled = enabledModules.has(moduleKey);

              return (
                <Card key={moduleKey} className="flex items-center justify-between p-4">
                  <div className="flex-1">
                    <div className="font-medium">{label}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Controlled at workspace level
                    </div>
                  </div>
                  <Switch
                    checked={isEnabled}
                    onCheckedChange={() => handleToggle(moduleKey)}
                    disabled={updateMutation.isPending}
                    aria-label={`Toggle ${label}`}
                  />
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
